"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { CheckCircle, AlertTriangle, Download } from "lucide-react";
import {
  useWallet as useAdapterWallet,
  WalletNotSelectedError,
} from "@solana/wallet-adapter-react";
import { Connection, PublicKey } from "@solana/web3.js";
import {
  parseLocalStorage,
  setLocalStorage,
  removeLocalStorage,
} from "../utils/localStorage";

import { normalizeSignAndSendSignature, resolveWalletSignTransactionFn, resolveWalletSignAllTransactionsFn } from "../utils/walletTransactionHelpers";
import {
  WALLET_DISCONNECTED_EVENT,
  WALLET_CONNECTION_UPDATED_EVENT,
  dispatchWalletDisconnected,
  dispatchWalletConnectionUpdated,
  clearWalletUserDisconnected,
  isWalletUserDisconnected,
  markWalletUserDisconnected,
} from "../utils/walletConnectAttention";
import { reportSystemError } from "@/lib/systemErrors/reportSystemError";
import {
  getInjectedSolanaProvider,
  hasMobileAppWallet,
  isMobileDevice,
  isWalletAppUserAgent,
  openWalletInAppBrowser,
  waitForInjectedSolanaProvider,
} from "@/utils/mobileWalletBrowse";
import { SOLANA_RPC_URL, resolveBrowserFallbackSolanaRpcUrl, createBrowserSolanaConnection } from "../config/rpc";
import {
  initMetaMaskSolanaClient,
  connectMetaMaskSolanaDirect,
  disconnectMetaMaskSolanaDirect,
  getMetaMaskSolanaDirectProvider,
  isMetaMaskInstalledSync,
  detectMetaMaskInstalled,
} from "../config/metamaskSolana";

const DEFAULT_RPC = SOLANA_RPC_URL;

function formatWalletConnectError(err, walletId) {
  const message = err?.message || String(err);
  const lower = message.toLowerCase();

  if (walletId === "metamask") {
    if (
      lower.includes("not installed") ||
      lower.includes("please install")
    ) {
      return message;
    }
    if (
      lower.includes("wallet_requestsnaps") ||
      lower.includes("wallet_invokesnap") ||
      lower.includes("does not exist") ||
      lower.includes("not available")
    ) {
      return (
        "MetaMask is not installed or is outdated. Install the MetaMask browser extension " +
        "and update to the latest version, then refresh the page."
      );
    }
    if (
      lower.includes("failed to initialize") ||
      lower.includes("no solana account") ||
      lower.includes("could not be detected")
    ) {
      return message;
    }
    if (lower.includes("user rejected") || lower.includes("rejected")) {
      return "MetaMask connection was cancelled.";
    }
    if (lower.includes("unexpected error")) {
      return (
        "MetaMask could not connect to Solana. Update MetaMask to the latest version, " +
        "unlock your wallet, and try again."
      );
    }
  }

  return message;
}

const useWallet = () => {
  /* ───────────────────────── Adapter Wallet State ───────────────────────── */
  const {
    wallets: adapterWallets,
    wallet: selectedAdapter,
    publicKey: solanaPublicKey,
    connected: isSolanaAdapterConnected,
    connecting: isSolanaAdapterConnecting,
    disconnect: adapterDisconnect,
    select: adapterSelect,
    sendTransaction: adapterSendTransaction,
    signTransaction: adapterSignTransaction,
    signAllTransactions: adapterSignAllTransactions,
  } = useAdapterWallet();

  /* ───────────────────────── Local State ───────────────────────── */
  const [error, setError] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);  
  const [balance, setBalance] = useState("0.0000");

  // direct wallets: coinbase, metamask, trust, brave, jupiter, …
  const [directConnectionState, setDirectConnectionState] = useState(null);
  /** Mirrors session flag so UI re-renders when user disconnects / reconnects. */
  const [userDisconnected, setUserDisconnected] = useState(() =>
    typeof window !== "undefined" ? isWalletUserDisconnected() : false,
  );

  const connectLockRef = useRef(false);
  const disconnectLockRef = useRef(false);
  const [metaMaskInstalled, setMetaMaskInstalled] = useState(() =>
    typeof window !== "undefined" ? isMetaMaskInstalledSync() : false
  );

  const persistDirectConnection = useCallback((provider, walletAddress) => {
    if (!provider || !walletAddress) return;
    clearWalletUserDisconnected();
    setUserDisconnected(false);
    const connectionState = {
      provider,
      walletAddress,
      isConnected: true,
    };
    setDirectConnectionState(connectionState);
    setLocalStorage("walletConnection", connectionState);
    dispatchWalletConnectionUpdated();
  }, []);

  const connection = useMemo(
    () => createBrowserSolanaConnection(DEFAULT_RPC),
    []
  );
  const fallbackRpcConnection = useMemo(() => {
    if (typeof window === "undefined") return null;
    const url = resolveBrowserFallbackSolanaRpcUrl(window.location.origin);
    return url ? createBrowserSolanaConnection(url) : null;
  }, []);

  /* ───────────────────────── Utilities (MUST EXIST) ───────────────────────── */
  const clearError = useCallback(() => setError(""), []);

  const formatAddress = useCallback((address) => {
    if (!address) return "";
    const str = typeof address === "string" ? address : address.toString?.();
    if (!str) return "";
    return `${str.slice(0, 4)}...${str.slice(-4)}`;
  }, []);

  const normalizeWalletId = useCallback((value) => {
    if (!value) return "";
    return String(value).toLowerCase().replace(/[^a-z0-9]/g, "");
  }, []);

  const copyAddress = useCallback(
    async (addr) => {
      try {
        const toCopy =
          addr ||
          solanaPublicKey?.toString() ||
          directConnectionState?.walletAddress;
        if (!toCopy) return false;
        await navigator.clipboard.writeText(toCopy);
        return true;
      } catch {
        return false;
      }
    },
    [solanaPublicKey, directConnectionState]
  );

  const [injectedWalletTick, setInjectedWalletTick] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let stopped = false;
    const bump = () => {
      if (!stopped) setInjectedWalletTick((tick) => tick + 1);
    };
    const events = ["phantom#initialized", "solflare#initialized", "backpack#initialized"];
    events.forEach((name) => window.addEventListener(name, bump));
    const poll = window.setInterval(() => {
      if (
        getInjectedSolanaProvider("phantom") ||
        getInjectedSolanaProvider("solflare") ||
        getInjectedSolanaProvider("backpack")
      ) {
        bump();
        window.clearInterval(poll);
      }
    }, 300);
    const stop = window.setTimeout(() => window.clearInterval(poll), 8000);
    return () => {
      stopped = true;
      events.forEach((name) => window.removeEventListener(name, bump));
      window.clearInterval(poll);
      window.clearTimeout(stop);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    detectMetaMaskInstalled().then((installed) => {
      if (!cancelled) setMetaMaskInstalled(installed);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ───────────────────────── Wallet Detection (CORRECT) ───────────────────────── */
  const detectWallets = useCallback(() => {
    if (typeof window === "undefined") {
      return {
        adapterWallets: adapterWallets.map((w) => w.adapter.name),
        coinbase: false,
        metamask: false,
        trust: false,
        brave: false,
        jupiter: false,
        idxWallet: false,
      };
    }

    return {
      adapterWallets: adapterWallets.map((w) => w.adapter.name),
      coinbase: !!window?.coinbaseSolana,
      metamask: metaMaskInstalled,
      trust: !!window?.trustwallet?.solana || (window?.solana?.isTrust && !!window?.solana),
      brave: !!window?.braveSolana,
      jupiter: !!window?.jupiter?.solana,
      idxWallet: !!(
        (window?.solana?.isIDXWallet && window?.solana) ||
        window?.IdxWallet ||
        window?.idxWallet
      ),
    };
  }, [adapterWallets, metaMaskInstalled]);

  const isWalletInstalled = useCallback(
    (id) => {
      if (typeof window === "undefined") return false;
      if (!id) return false;

      const normalizedId = normalizeWalletId(id);

      // MetaMask is handled directly — wallet-standard registration can make it
      // appear "Installed" even when the extension is missing.
      if (normalizedId === "metamask") {
        return metaMaskInstalled;
      }

      // Mobile in-app browsers inject window.phantom / window.solflare. That
      // provider is the wallet, even when the desktop adapter never flips to Installed.
      if (hasMobileAppWallet(normalizedId)) return true;

      const adapter = adapterWallets.find(
        (w) => normalizeWalletId(w.adapter.name) === normalizedId
      );
      if (adapter) return adapter.readyState === "Installed";

      switch (id) {
        case "coinbase":
          return !!window?.coinbaseSolana;
        case "trustwallet":
          return !!window?.trustwallet?.solana || (window?.solana?.isTrust && !!window?.solana);
        case "brave":
          return !!window?.braveSolana;
        case "jupiter":
          return !!window?.jupiter?.solana;
        case "idxwallet":
          return !!(
            (window?.solana?.isIDXWallet && window?.solana) ||
            window?.IdxWallet ||
            window?.idxWallet
          );
        default:
          return false;
      }
    },
    [adapterWallets, injectedWalletTick, normalizeWalletId, metaMaskInstalled]
  );

  const supportsSolana = useCallback(() => true, []);

  /* ───────────────────────── UI Helpers (shared lists & statuses) ───────────────────────── */
  const walletProviders = useMemo(
    () => {
      const providers = [
        {
          id: "idxwallet",
          name: "IDX Wallet",
          icon: (
            <img
              src="/icon-512x512.png"
              alt="IDX Wallet"
              className="w-10 h-10 rounded-lg"
            />
          ),
          installed: isWalletInstalled("idxwallet"),
          description: "IDX Web3 Wallet",
          network: "solana",
          supportsSolana: true,
          recommended: true,
          downloadUrl: "https://idxwallet.net",
        },
        {
          id: "phantom",
          name: "Phantom",
          icon: (
            <img
              src="https://mintlify.s3.us-west-1.amazonaws.com/phantom-e50e2e68/resources/images/Phantom_SVG_Icon.svg"
              alt="Phantom"
              className="w-10 h-10 rounded-lg"
            />
          ),
          installed: isWalletInstalled("phantom"),
          description: "Popular Solana wallet",
          network: "solana",
          supportsSolana: true,
          downloadUrl: "https://phantom.app/download",
        },
        {
          id: "solflare",
          name: "Solflare",
          icon: (
            <img
              src="https://www.solflare.com/wp-content/uploads/2024/11/App-Icon.svg"
              alt="Solflare"
              className="w-10 h-10"
            />
          ),
          installed: isWalletInstalled("solflare"),
          description: "Solana-native wallet",
          network: "solana",
          supportsSolana: true,
          downloadUrl: "https://solflare.com/download",
        },
        {
          id: "coinbase",
          name: "Coinbase Wallet",
          icon: (
            <img
              src="https://raw.githubusercontent.com/gist/taycaldwell/2291907115c0bb5589bc346661435007/raw/280eafdc84cb80ed0c60e36b4d0c563f6dca6b3e/cbw.svg"
              alt="Coinbase"
              className="w-10 h-10"
            />
          ),
          installed: isWalletInstalled("coinbase"),
          description: "Multi-chain wallet with Solana",
          network: "solana",
          supportsSolana: true,
          downloadUrl: "https://www.coinbase.com/en-in/wallet",
        },
        {
          id: "backpack",
          name: "Backpack",
          icon: (
            <img
              src="https://backpack.app/favicon.ico"
              alt="Backpack"
              className="w-10 h-10"
            />
          ),
          installed: isWalletInstalled("backpack"),
          description: "xNFT & Solana wallet",
          network: "solana",
          supportsSolana: true,
          downloadUrl: "https://backpack.app/download",
        },
        {
          id: "brave",
          name: "Brave Wallet",
          icon: (
            <img
              src="https://brave.com/favicon.ico"
              alt="Brave Wallet"
              className="w-10 h-10"
            />
          ),
          installed: isWalletInstalled("brave"),
          description: "Built-in Brave wallet (Solana)",
          network: "solana",
          supportsSolana: true,
          downloadUrl: "https://brave.com/wallet/",
        },
        {
          id: "metamask",
          name: "MetaMask",
          icon: (
            <img
              src="https://metamask.io/favicon.ico"
              alt="MetaMask"
              className="w-10 h-10"
            />
          ),
          installed: isWalletInstalled("metamask"),
          description: "Native Solana support in MetaMask",
          network: "solana",
          supportsSolana: true,
          downloadUrl: "https://metamask.io/download",
        },
        {
          id: "jupiter",
          name: "Jupiter",
          icon: (
            <img
              src="https://www.jup.ag/favicon.ico"
              alt="Jupiter"
              className="w-10 h-10"
            />
          ),
          installed: isWalletInstalled("jupiter"),
          description: "Jupiter wallet with Solana support",
          network: "solana",
          supportsSolana: true,
          downloadUrl: "https://www.jup.ag/wallet",
        },
      ];

      // Sort: recommended wallets first, then installed wallets.
      return providers.sort((a, b) => {
        if (a.recommended && !b.recommended) return -1;
        if (!a.recommended && b.recommended) return 1;
        if (a.installed && !b.installed) return -1;
        if (!a.installed && b.installed) return 1;
        return 0; // Keep original order for same installation status
      });
    },
    [isWalletInstalled]
  );

  const getWalletStatus = useCallback((provider) => {
    if (provider.disabled) {
      return {
        icon: AlertTriangle,
        color: "text-red-400",
        text: "Not Supported",
      };
    }
    if (!provider.installed) {
      return { icon: Download, color: "text-orange-400", text: "Install" };
    }

    // Check if this wallet is currently connected
    const isCurrentlyConnected =
      (directConnectionState?.provider === provider.id) ||
      (selectedAdapter?.adapter?.name?.toLowerCase() === provider.id);

    if (isCurrentlyConnected) {
      return { icon: CheckCircle, color: "text-blue-400", text: "Connected" };
    }

    if (provider.supportsSolana) {
      return { icon: CheckCircle, color: "text-green-400", text: "Ready" };
    }
    return { icon: AlertTriangle, color: "text-yellow-400", text: "Limited" };
  }, [directConnectionState, selectedAdapter]);

  /* ───────────────────────── Hydrate from persisted storage ───────────────────────── */
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Only hydrate from localStorage if we don't have any connection state
    // This prevents overwriting active connections with stale data
    if (directConnectionState?.walletAddress || isSolanaAdapterConnected) return;
    if (isWalletUserDisconnected()) return;

    try {
      const saved = parseLocalStorage("walletConnection");
      if (saved?.walletAddress && saved?.provider) {
        setDirectConnectionState({
          provider: saved.provider,
          walletAddress: saved.walletAddress,
          isConnected: true,
        });
      }
    } catch (err) {
      console.warn("Wallet localStorage hydration failed:", err);
    }
  }, [directConnectionState, isSolanaAdapterConnected]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const syncFromPersisted = () => {
      if (isWalletUserDisconnected()) return;
      setUserDisconnected(false);
      if (isSolanaAdapterConnected && solanaPublicKey) return;
      try {
        const saved = parseLocalStorage("walletConnection");
        if (!saved?.walletAddress || !saved?.provider) return;
        setDirectConnectionState((prev) => {
          if (
            prev?.walletAddress === saved.walletAddress &&
            prev?.provider === saved.provider
          ) {
            return prev;
          }
          return {
            provider: saved.provider,
            walletAddress: saved.walletAddress,
            isConnected: true,
          };
        });
      } catch {
        /* noop */
      }
    };

    window.addEventListener(WALLET_CONNECTION_UPDATED_EVENT, syncFromPersisted);
    return () => {
      window.removeEventListener(WALLET_CONNECTION_UPDATED_EVENT, syncFromPersisted);
    };
  }, [isSolanaAdapterConnected, solanaPublicKey]);

  /* ───────────────────────── Wallet Connection Detection ───────────────────────── */
  const detectConnectedWallet = useCallback(() => {
    if (typeof window === "undefined") return null;

    // Adapter wallets via wallet-adapter React context
    if (isSolanaAdapterConnected && solanaPublicKey) {
      const providerId = selectedAdapter?.adapter?.name
        ? normalizeWalletId(selectedAdapter.adapter.name)
        : "adapter";
      return {
        provider: providerId,
        address: solanaPublicKey.toString(),
        source: "adapter",
      };
    }

    // Adapter instances — context `connected` can lag behind `adapter.connected`
    for (const w of adapterWallets) {
      const pk = w.adapter?.publicKey;
      if (w.adapter?.connected && pk) {
        return {
          provider: normalizeWalletId(w.adapter.name),
          address: pk.toString(),
          source: "adapter-instance",
        };
      }
    }

    // Coinbase Wallet - highest priority
    if (window?.coinbaseSolana?.publicKey) {
      return {
        provider: "coinbase",
        address: window.coinbaseSolana.publicKey.toString(),
        source: "coinbase"
      };
    }
    // Brave Wallet
    if (window?.braveSolana?.publicKey) {
      return {
        provider: "brave",
        address: window.braveSolana.publicKey.toString(),
        source: "brave"
      };
    }

    // Jupiter Wallet
    if (window?.jupiter?.solana?.publicKey) {
      return {
        provider: "jupiter",
        address: window.jupiter.solana.publicKey.toString(),
        source: "jupiter"
      };
    }

    // IDX Wallet
    if (window?.solana?.isIDXWallet && window?.solana?.publicKey) {
      return {
        provider: "idxwallet",
        address: window.solana.publicKey.toString(),
        source: "idxwallet",
      };
    }
    if (window?.IdxWallet?.publicKey) {
      return {
        provider: "idxwallet",
        address: window.IdxWallet.publicKey.toString(),
        source: "idxwallet",
      };
    }
    if (window?.idxWallet?.publicKey) {
      return {
        provider: "idxwallet",
        address: window.idxWallet.publicKey.toString(),
        source: "idxwallet",
      };
    }

    return null;
  }, [
    adapterWallets,
    isSolanaAdapterConnected,
    solanaPublicKey,
    selectedAdapter,
    normalizeWalletId,
  ]);

  // Sync wallet connection state
  const syncWalletConnection = useCallback((detectedWallet) => {
    if (!detectedWallet) {
      // Do not auto-clear here. Multiple useWallet() instances share localStorage;
      // clearing on a missed detect raced with connects and wiped the header state.
      return;
    }

    // User explicitly disconnected — do not re-attach from extension still being "connected".
    if (isWalletUserDisconnected()) return;

    // Only update if the detected wallet is different from current state
    const isDifferentWallet =
      !directConnectionState ||
      directConnectionState.provider !== detectedWallet.provider ||
      directConnectionState.walletAddress !== detectedWallet.address;

    if (isDifferentWallet) {
      persistDirectConnection(detectedWallet.provider, detectedWallet.address);
    }
  }, [directConnectionState, persistDirectConnection]);

  // Check and sync wallet connections with debouncing
  const checkWalletConnections = useCallback(() => {
    const detected = detectConnectedWallet();
    syncWalletConnection(detected);
  }, [detectConnectedWallet, syncWalletConnection]);

  // Debounced version to prevent excessive checks
  const debouncedCheck = useCallback(
    (() => {
      let timeoutId;
      return () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(checkWalletConnections, 100);
      };
    })(),
    [checkWalletConnections]
  );

  // Initialize wallet detection
  useEffect(() => {
    // Initial check
    checkWalletConnections();

    // Check on window focus and visibility change (user might switch back from wallet)
    const handleFocus = () => debouncedCheck();
    const handleVisibilityChange = () => {
      if (!document.hidden) debouncedCheck();
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkWalletConnections, debouncedCheck]);

  // Keep every `useWallet` instance in sync when disconnect happens elsewhere (e.g. header).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onDisconnected = () => {
      setUserDisconnected(true);
      setDirectConnectionState(null);
      clearError();
    };
    window.addEventListener(WALLET_DISCONNECTED_EVENT, onDisconnected);
    return () => {
      window.removeEventListener(WALLET_DISCONNECTED_EVENT, onDisconnected);
    };
  }, [clearError]);

  // Drop adapter-persisted direct state when wallet-adapter disconnects globally.
  useEffect(() => {
    if (isSolanaAdapterConnected || solanaPublicKey) return;
    setDirectConnectionState((prev) => {
      if (!prev?.walletAddress) return prev;
      const adapterNames = new Set(
        adapterWallets.map((w) => normalizeWalletId(w.adapter.name))
      );
      if (!adapterNames.has(prev.provider)) return prev;
      return null;
    });
  }, [
    isSolanaAdapterConnected,
    solanaPublicKey,
    adapterWallets,
    normalizeWalletId,
  ]);

  // Keep header/UI in sync when wallet-adapter connects (Phantom via Wallet Standard, Solflare, etc.)
  useEffect(() => {
    if (!isSolanaAdapterConnected || !solanaPublicKey) return;
    // Intentional disconnect: adapter may still report connected until fully torn down —
    // do not re-persist and flip the app back to connected.
    if (isWalletUserDisconnected()) return;
    setUserDisconnected(false);
    const address = solanaPublicKey.toString();
    const providerId = selectedAdapter?.adapter?.name
      ? normalizeWalletId(selectedAdapter.adapter.name)
      : "adapter";
    setDirectConnectionState((prev) => {
      if (prev?.walletAddress === address && prev?.provider === providerId) {
        return prev;
      }
      const next = {
        provider: providerId,
        walletAddress: address,
        isConnected: true,
      };
      setLocalStorage("walletConnection", next);
      dispatchWalletConnectionUpdated();
      return next;
    });
  }, [
    isSolanaAdapterConnected,
    solanaPublicKey,
    selectedAdapter,
    normalizeWalletId,
  ]);

  /* ───────────────────────── Connect Logic (OPTIMIZED) ───────────────────────── */
  const connectWallet = useCallback(async (walletId) => {
    // Prevent concurrent connections
    if (connectLockRef.current || isConnecting) return;
    connectLockRef.current = true;
    setIsConnecting(true);
    clearError();
    clearWalletUserDisconnected();
    setUserDisconnected(false);

    try {
      const normalizedWalletId = normalizeWalletId(walletId);

      const inWalletApp = isWalletAppUserAgent(normalizedWalletId);
      let injected = getInjectedSolanaProvider(normalizedWalletId);
      if ((isMobileDevice() || inWalletApp) && (injected || inWalletApp)) {
        if (!injected) {
          injected = await waitForInjectedSolanaProvider(normalizedWalletId);
        }
        if (injected?.connect) {
          const connectResult = await injected.connect();
          const publicKey = connectResult?.publicKey || injected.publicKey;
          if (!publicKey) {
            throw new Error(`${normalizedWalletId} connected but no publicKey found`);
          }
          persistDirectConnection(normalizedWalletId, publicKey.toString());
          return;
        }
      }

      if (
        !isWalletInstalled(normalizedWalletId) &&
        openWalletInAppBrowser(normalizedWalletId)
      ) {
        return;
      }

      // MetaMask uses @metamask/connect-solana directly (not wallet-adapter).
      if (walletId === "metamask") {
        if (!metaMaskInstalled) {
          throw new Error(
            "MetaMask is not installed. Please install the MetaMask browser extension and try again."
          );
        }

        await initMetaMaskSolanaClient();
        const address = await connectMetaMaskSolanaDirect();
        persistDirectConnection("metamask", address);
        return;
      }

      // Adapter wallets (Phantom, Solflare, Backpack, Coinbase, …)
      const adapter = adapterWallets.find(
        (w) => normalizeWalletId(w.adapter.name) === normalizedWalletId
      );

      if (adapter) {
        adapterSelect(adapter.adapter.name);
        if (!adapter.adapter.connected) {
          await adapter.adapter.connect();
        }
        const adapterPublicKey = adapter.adapter.publicKey?.toString?.();
        if (adapterPublicKey) {
          persistDirectConnection(normalizedWalletId, adapterPublicKey);
        }
        return;
      }

      // 2️⃣ Direct wallet providers mapping
      const walletProviders = {
        coinbase: {
          provider: window?.coinbaseSolana,
          errorMsg: "Coinbase Wallet not installed"
        },
        brave: {
          provider: window?.braveSolana,
          errorMsg: "Brave Wallet not installed"
        },
        jupiter: {
          provider: window?.jupiter?.solana,
          errorMsg: "Jupiter not installed"
        },
        idxwallet: {
          provider:
            (window?.solana?.isIDXWallet && window?.solana) ||
            window?.IdxWallet ||
            window?.idxWallet,
          errorMsg: "IDX Wallet not installed",
        },
      };

      // Handle direct wallets
      const walletConfig = walletProviders[walletId];
      if (walletConfig) {
        const { provider, errorMsg } = walletConfig;
        if (!provider) {
          throw new Error(errorMsg);
        }

        // Connect to wallet
        const connectResult = await provider.connect();
        const publicKey = connectResult?.publicKey || provider.publicKey;

        if (!publicKey) {
          throw new Error(`${walletId} connected but no publicKey found`);
        }

        persistDirectConnection(walletId, publicKey.toString());
        return;
      }

      throw new Error(`Wallet ${walletId} not supported or not installed`);
    } catch (err) {
      const errorMessage = formatWalletConnectError(err, walletId);
      setError(errorMessage);
      reportSystemError({
        phase: "wallet",
        code: "WALLET_CONNECT_FAILED",
        message: errorMessage,
        details: { walletId: walletId || null },
      });
      throw new Error(errorMessage);
    } finally {
      connectLockRef.current = false;
      setIsConnecting(false);
    }
  }, [
    adapterWallets,
    adapterSelect,
    clearError,
    isConnecting,
    isWalletInstalled,
    metaMaskInstalled,
    normalizeWalletId,
    persistDirectConnection,
  ]);

  /* ───────────────────────── Shared connect handler for UI components ───────────────────────── */
  const handleWalletConnection = useCallback(
    async (providerId) => {
      clearError();

      const provider = walletProviders.find((p) => p.id === providerId);
      if (!provider) {
        throw new Error(`Unknown wallet provider: ${providerId}`);
      }

      if (!provider.supportsSolana) {
        throw new Error(
          `${provider.name} does not support Solana. Please use a Solana-compatible wallet.`
        );
      }

      if (!provider.installed) {
        if (openWalletInAppBrowser(provider.id)) return provider;
        if (provider.downloadUrl) {
          window.open(provider.downloadUrl, "_blank");
        }
        throw new Error(`${provider.name} is not installed.`);
      }

      await connectWallet(providerId);
      return provider;
    },
    [walletProviders, connectWallet, clearError]
  );

  /* ───────────────────────── Disconnect Logic (OPTIMIZED) ───────────────────────── */
  const disconnectWallet = useCallback(async () => {
    // Prevent concurrent disconnect operations
    if (disconnectLockRef.current) return;
    disconnectLockRef.current = true;
    clearError();

    const directProvider = directConnectionState?.provider ?? null;
    const hadAdapter = Boolean(selectedAdapter && isSolanaAdapterConnected);

    // Mark disconnected immediately so debouncedCheck cannot re-persist during async adapter teardown.
    markWalletUserDisconnected();
    setUserDisconnected(true);
    setDirectConnectionState(null);
    removeLocalStorage("walletConnection");
    dispatchWalletDisconnected();

    try {
      if (hadAdapter) {
        try {
          await adapterDisconnect();
        } catch (err) {
          if (!(err instanceof WalletNotSelectedError)) {
            console.warn("Adapter disconnect error:", err);
          }
        }
      }

      if (directProvider) {
        const disconnectHandlers = {
          coinbase: async () => {
            try {
              await window?.coinbaseSolana?.disconnect?.();
            } catch {
              /* Coinbase may not support disconnect cleanly */
            }
          },
          brave: async () => {
            try {
              await window?.braveSolana?.disconnect?.();
            } catch {
              /* Brave may not support disconnect cleanly */
            }
          },
          jupiter: async () => {
            try {
              await window?.jupiter?.solana?.disconnect?.();
            } catch {
              /* Jupiter may not support disconnect cleanly */
            }
          },
          idxwallet: async () => {
            try {
              if (window?.solana?.isIDXWallet) {
                await window?.solana?.disconnect?.();
              } else if (window?.IdxWallet) {
                await window?.IdxWallet?.disconnect?.();
              } else {
                await window?.idxWallet?.disconnect?.();
              }
            } catch {
              /* IDX may not support disconnect cleanly */
            }
          },
          metamask: async () => {
            try {
              await disconnectMetaMaskSolanaDirect();
            } catch {
              /* MetaMask may not support disconnect cleanly */
            }
          },
        };

        const disconnectHandler = disconnectHandlers[directProvider];
        if (disconnectHandler) {
          await disconnectHandler();
        }
      }
    } catch (err) {
      console.warn("Disconnect error:", err);
    } finally {
      disconnectLockRef.current = false;
    }
  }, [
    adapterDisconnect,
    selectedAdapter,
    isSolanaAdapterConnected,
    directConnectionState,
    clearError,
  ]);

  /* ───────────────────────── Balance ───────────────────────── */
  const getSolanaBalance = useCallback(
    async (pubKey) => {
      try {
        const pk =
          pubKey ||
          solanaPublicKey ||
          (directConnectionState?.walletAddress
            ? new PublicKey(directConnectionState.walletAddress)
            : null);

        if (!pk) return "0.0000";
        const key = typeof pk === "string" ? new PublicKey(pk) : pk;
        try {
          const lamports = await connection.getBalance(key);
          return (lamports / 1e9).toFixed(4);
        } catch {
          if (fallbackRpcConnection) {
            const lamports = await fallbackRpcConnection.getBalance(key);
            return (lamports / 1e9).toFixed(4);
          }
          return "0.0000";
        }
      } catch {
        return "0.0000";
      }
    },
    [connection, fallbackRpcConnection, solanaPublicKey, directConnectionState]
  );

  /* ───────────────────────── Final Derived State ───────────────────────── */
  const finalAddress = userDisconnected
    ? ""
    : solanaPublicKey?.toString() || directConnectionState?.walletAddress || "";

  // Normalize wallet names to match UI expectations
  const normalizeWalletName = (provider) => {
    switch (provider) {
      case "coinbase":
        return "Coinbase Wallet";
      case "brave":
        return "Brave Wallet";
      case "jupiter":
        return "Jupiter";
      case "idxwallet":
        return "IDX Wallet";
      case "metamask":
        return "MetaMask";
      default:
        return provider;
    }
  };

  const connectedWalletName =
    selectedAdapter?.adapter?.name ||
    (directConnectionState?.provider
      ? normalizeWalletName(directConnectionState.provider)
      : null);

  const resolveSignTransactionFn = useCallback(
    () =>
      resolveWalletSignTransactionFn({
        isSolanaAdapterConnected,
        adapterSignTransaction,
        adapterWallets,
        directProviderId: directConnectionState?.provider,
        connectedWalletName,
        getMetaMaskSolanaDirectProvider,
      }),
    [
      adapterSignTransaction,
      adapterWallets,
      connectedWalletName,
      directConnectionState?.provider,
      isSolanaAdapterConnected,
    ],
  );

  const resolveSignAllTransactionsFn = useCallback(
    () =>
      resolveWalletSignAllTransactionsFn({
        isSolanaAdapterConnected,
        adapterSignAllTransactions,
        adapterWallets,
        directProviderId: directConnectionState?.provider,
        connectedWalletName,
        getMetaMaskSolanaDirectProvider,
      }),
    [
      adapterSignAllTransactions,
      adapterWallets,
      connectedWalletName,
      directConnectionState?.provider,
      isSolanaAdapterConnected,
    ],
  );

  const canSignTransaction = useMemo(
    () => Boolean(resolveSignTransactionFn()),
    [resolveSignTransactionFn],
  );

  const getDirectWalletProvider = useCallback(() => {
    if (typeof window === "undefined" || !directConnectionState?.provider) {
      return null;
    }
    const providerId = directConnectionState.provider;
    const providers = {
      phantom: getInjectedSolanaProvider("phantom"),
      solflare: getInjectedSolanaProvider("solflare"),
      backpack: getInjectedSolanaProvider("backpack"),
      coinbase: window.coinbaseSolana,
      brave: window.braveSolana,
      jupiter: window.jupiter?.solana,
      metamask: getMetaMaskSolanaDirectProvider(),
      idxwallet:
        (window.solana?.isIDXWallet && window.solana) ||
        window.IdxWallet ||
        window.idxWallet,
    };
    return providers[providerId] || null;
  }, [directConnectionState]);

  /** Sign via wallet-adapter, connected adapter instance, or injected provider. */
  const signTransaction = useCallback(
    async (transaction) => {
      const signFn = resolveSignTransactionFn();
      if (signFn) return signFn(transaction);
      throw new Error("Wallet does not support signTransaction");
    },
    [resolveSignTransactionFn],
  );

  const signAllTransactions = useCallback(
    async (transactions) => {
      const signAllFn = resolveSignAllTransactionsFn();
      if (signAllFn) return signAllFn(transactions);
      if (!Array.isArray(transactions) || !transactions.length) {
        throw new Error("Wallet does not support signAllTransactions");
      }
      const signFn = resolveSignTransactionFn();
      if (!signFn) throw new Error("Wallet does not support signAllTransactions");
      const out = [];
      for (const tx of transactions) {
        out.push(await signFn(tx));
      }
      return out;
    },
    [resolveSignAllTransactionsFn, resolveSignTransactionFn],
  );

  const sendTransaction = useCallback(
    async (transaction, conn, options) => {
      const connToUse = conn || connection;

      if (isSolanaAdapterConnected && adapterSendTransaction) {
        const out = await adapterSendTransaction(transaction, connToUse, options);
        return normalizeSignAndSendSignature(out);
      }

      const direct = getDirectWalletProvider();
      if (direct?.signAndSendTransaction) {
        const out = await direct.signAndSendTransaction(transaction, options);
        return normalizeSignAndSendSignature(out);
      }
      if (direct?.signTransaction) {
        const signed = await direct.signTransaction(transaction);
        const out = await connToUse.sendRawTransaction(
          signed.serialize(),
          options || { skipPreflight: false }
        );
        return normalizeSignAndSendSignature(out);
      }

      throw new Error("Wallet does not support sendTransaction");
    },
    [
      adapterSendTransaction,
      isSolanaAdapterConnected,
      connection,
      getDirectWalletProvider,
    ]
  );

  /* ───────────────────────── Return (UNCHANGED SHAPE) ───────────────────────── */
  return {
    isConnected:
      !userDisconnected &&
      (Boolean(isSolanaAdapterConnected && solanaPublicKey) ||
        Boolean(directConnectionState?.walletAddress)),
    walletAddress: finalAddress,
    balance,
    connectedWallet: userDisconnected ? null : connectedWalletName,
    network: "solana",
    isConnecting: isConnecting || isSolanaAdapterConnecting,
    error,
    walletType: "solana",

    connect: connectWallet,
    handleWalletConnection,
    disconnect: disconnectWallet,

    detectWallets,
    isWalletInstalled,
    supportsSolana,
    formatAddress,
    copyAddress,
    clearError,
    walletProviders,
    getWalletStatus,

    solanaPublicKey: userDisconnected ? null : solanaPublicKey,
    selectedAdapter,
    sendTransaction,
    signTransaction,
    signAllTransactions,
    canSignTransaction,
    connection,
    getSolanaBalance,
  };
};

export {
  normalizeSignAndSendSignature,
  isUserRejectedWalletError,
  resolveSignAndSendProvider,
} from "../utils/walletTransactionHelpers";

export default useWallet;